import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { Command, CommandInput, CommandItem, CommandList, CommandEmpty } from '@/components/ui/command';
import type { HoldingsBenchmarkCategory } from 'venn-api';
import { cn } from '@/lib/utils';
import { Icon } from 'venn-ui-kit';
import { useQuery } from '@tanstack/react-query';
import { getHoldingsCategories } from 'venn-api';
import { createFixedStrategiesKey, useHasFF } from 'venn-utils';
import { extractLeafStrategies } from '../../utils/strategyUtils';

interface StrategyAssignmentPopoverProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onStrategySelect: (strategyId: string, strategyName: string) => void;
  investmentName: string;
  children: React.ReactNode;
  disabled: boolean | undefined;
  align?: 'center' | 'start' | 'end';
  alignOffset?: number;
  isAnchored?: boolean;
  className?: string;
}

export const StrategyAssignmentPopover: React.FC<StrategyAssignmentPopoverProps> = ({
  isOpen,
  onOpenChange,
  onStrategySelect,
  investmentName,
  className,
  children,
  align = 'center',
  alignOffset = 0,
  disabled = false,
  isAnchored = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const hasManualStrategyAssignment = useHasFF('rcm_strategy_enforcement_ff');

  const { data: categories = [], isFetching } = useQuery<HoldingsBenchmarkCategory[]>(
    createFixedStrategiesKey('ASSET', 'WORKSPACE'),
    async () => (await getHoldingsCategories('ASSET', 'WORKSPACE')).content || [],
    { staleTime: 10 * 60 * 1000, keepPreviousData: true, enabled: hasManualStrategyAssignment },
  );

  const availableStrategies = useMemo(() => extractLeafStrategies(categories), [categories]);

  const filteredStrategies = useMemo(() => {
    if (!searchQuery) return availableStrategies;

    const query = searchQuery.toLowerCase();
    return availableStrategies.filter((strategy) => strategy.categoryName?.toLowerCase().includes(query));
  }, [availableStrategies, searchQuery]);

  const handleStrategySelect = (strategy: HoldingsBenchmarkCategory) => {
    if (strategy.id && strategy.categoryName) {
      onStrategySelect(strategy.id?.id, strategy.categoryName);
      onOpenChange(false);
      setSearchQuery('');
    }
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setSearchQuery('');
    }
  };

  const classValue =
    className || 'mr-[2px] w-[778px] h-[500px] p-0 border-2 border-solid border-venn-error rounded-[8px]';

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {!isAnchored && (
        <PopoverTrigger asChild disabled={disabled || isFetching}>
          {children}
        </PopoverTrigger>
      )}
      {isAnchored && <PopoverAnchor>{children}</PopoverAnchor>}
      <PopoverContent className={classValue} align={align} alignOffset={alignOffset} side="bottom" sideOffset={0}>
        <div className="flex flex-col h-full">
          <div className="flex p-[16px] items-center justify-between">
            <h3 className="text-[16px] m-0">
              <span className="font-bold">Assign level 4 strategy to:</span> {investmentName}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-[18px] w-[18px] p-0 text-black hover:bg-gray-100"
              onClick={() => handleOpenChange(false)}
            >
              <Icon type="close" className="text-[18px]" />
            </Button>
          </div>

          <div className="flex-1 overflow-hidden">
            <Command className="h-full" shouldFilter={false}>
              <div className="relative px-[16px] pb-[16px]">
                <CommandInput placeholder="Search for a strategy…" value={searchQuery} onValueChange={setSearchQuery} />
              </div>

              <CommandList className="flex-1 overflow-y-auto max-h-[full]">
                <CommandEmpty className="p-[16px] text-center text-gray-500">No strategies found.</CommandEmpty>

                {filteredStrategies.map((strategy) => (
                  <CommandItem
                    key={strategy.id?.id}
                    value={strategy.categoryName || ''}
                    onSelect={() => handleStrategySelect(strategy)}
                    className={cn(
                      'py-[8px] cursor-pointer hover:bg-venn-grey-10 border-0 border-solid border-b border-venn-grey-30 last:border-b-0',
                      'flex items-center justify-start text-left text-[14px] px-[16px]',
                    )}
                  >
                    {strategy.categoryName}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
